import React from "react";
import { Box, Stack, Typography, CircularProgress } from "@mui/material";
import moment from "moment";
import { useGetSalesSummaryQuery } from "store/services/reportApi";

const SalesSummary = ({
  timeRange = "last7days",
  customFromDate = null,
  customToDate = null,
  keyword = "",
  account,
  entity,
  branch = "all",
  payer = "all",
  user = "all",
  responsibleUser = "all",
  region = "all",
  customer = "all",
  vendor = "all",
  relationshipManager = "all",
  paymentStatus = "all",
}) => {
  const computeDates = () => {
    const today = moment().startOf("day");
    let fromDate;
    let toDate;

    switch (timeRange) {
      case "today":
        fromDate = today.format("MM-DD-YYYY");
        toDate = today.endOf("day").format("MM-DD-YYYY");
        break;
      case "last7days":
        fromDate = moment()
          .subtract(6, "days")
          .startOf("day")
          .format("MM-DD-YYYY");
        toDate = today.endOf("day").format("MM-DD-YYYY");
        break;
      case "oneMonth":
        fromDate = moment()
          .subtract(1, "month")
          .startOf("day")
          .format("MM-DD-YYYY");
        toDate = today.endOf("day").format("MM-DD-YYYY");
        break;
      case "custom":
        fromDate = customFromDate
          ? moment(customFromDate).format("MM-DD-YYYY")
          : null;
        toDate = customToDate
          ? moment(customToDate).format("MM-DD-YYYY")
          : null;
        break;
      case "all":
      default:
        fromDate = null;
        toDate = null;
        break;
    }

    return { fromDate, toDate };
  };

  const { fromDate, toDate } = computeDates();
  const isPendingCustomRange = timeRange === "custom" && (!fromDate || !toDate);
  const { data, isLoading, isFetching, error } = useGetSalesSummaryQuery(
    {
      params: {
        fromDate,
        toDate,
        ...(keyword && { keyword }),
        account,
        entity,
        branch: branch !== "all" ? branch : undefined,
        payer: payer !== "all" ? payer : undefined,
        user: user !== "all" ? user : undefined,
        region: region !== "all" ? region : undefined,
        responsibleUser: responsibleUser !== "all" ? responsibleUser : undefined,
        customer: customer !== "all" ? customer : undefined,
        vendor: vendor !== "all" ? vendor : undefined,
        relationshipManager:
          relationshipManager !== "all" ? relationshipManager : undefined,
        paymentStatus,
        sortedBy: "createdAt",
        sortDirection: -1,
      },
    },
    { skip: isPendingCustomRange }
  );
  const summary = data || {};
  if (isPendingCustomRange) {
    return null;
  }
  if (isLoading || isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography variant="body2" color="error">
          Failed to load summary.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 2 }}>
      <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total Paid
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            Rs. {Number(summary.totalRevenue || 0).toFixed(2)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total Not Paid
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            Rs. {Number(summary.totalReceivables || 0).toFixed(2)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total Collected
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            Rs. {Number(summary.totalCollected || 0).toFixed(2)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total Sales
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            Rs. {Number(summary.totalAmount || 0).toFixed(2)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default SalesSummary;
